import React from 'react';
import {
	Flex,
	Typography,
	Alert,
	Col,
} from 'antd';
import {
	Currencies,
	FixtureTypes,
	FuelTypes,
	PortActionTypes,
} from '@shared/utils/constants';
import { calculateTotal } from '@shared/utils/math';
import { Values } from '@shared/utils/objectEnums';
import {
	isPortCallCommencementDto,
	isPortCallCompletionDto,
	isPortCallDeliveryDto,
	isPortCallRedeliveryDto,
} from '@shared/utils/portCallHelpers';
import type { RobBunkerProps } from '@api/models/rob-bunker';
import type { GetRobsFromPortCallResponse } from '@api/features/vessels/getRobsFromPortCall';
import type { GetVoyageDetailsResponse } from '@api/features/voyages/getVoyageDetails';
import { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import EditableTable from '@client/components/EditableTable/EditableTable';
import Card from '@client/components/Card/Card';
import TooltipIcon from '@client/components/TooltipIcon';
import styles from '../tabs/styles/PortCallBunkersTab.module.css';
import { getRobTableColumns } from '../tabs/getColumns';
import { sortByFuelGrade } from './helpers';

export type NullableItineraryBunkerRecord = {
	id?: number | string;
	portCallActionId: number;
	quantity: number;
	fuelGrade: Values<typeof FuelTypes>;
	pricePerTon?: number;
	event?: Values<typeof PortActionTypes>;
};

export type ItineraryBunkerRecord = NullableItineraryBunkerRecord & { key: string | number };

export type CommencementRobBunker = NullableItineraryBunkerRecord & {
	key: string | number;
	robBunkerId?: number;
};

export type OnAddFn = (
	id: number | null | undefined,
	newRow: ItineraryBunkerRecord
) => Promise<void> | void;

export type OnSaveFn = (
	id: number | string | undefined,
	portCallActionId: number | undefined | null,
	newRow: ItineraryBunkerRecord
) => Promise<void> | void;

export type OnDeleteFn = (
	id: number | string | undefined,
	row: ItineraryBunkerRecord
) => Promise<void>;

export type OnRemoveFuelQueueItemFn = (
	row: CommencementRobBunker
) => Promise<void>;

export type OnSaveFuelQueueItem = (
	portCallActionId: number | string | undefined,
	robBunkerId: number,
	row: CommencementRobBunker[]
) => Promise<void>;

export type IncomingBunkerArray = Array<{
	id: number | string;
	key: string | number;
	portCallActionId: number;
	quantity: number;
	fuelGrade: Values<typeof FuelTypes>;
	pricePerTon: number;
	currency?: Values<typeof Currencies>;
}>;

export const getBunkerTable = ({
	robs,
	onSave,
	onAddNew,
	onDelete,
	allowAddNew = true,
	allowEdit = true,
	allowDelete = true,
	portCallActionId,
	event,
	showPricePerTon,
	title,
	titleTooltip,
}: {
	robs: IncomingBunkerArray;
	onSave: OnSaveFn;
	onAddNew: OnAddFn;
	onDelete: OnDeleteFn;
	portCallActionId: number | null | undefined;
	allowAddNew?: boolean;
	allowDelete?: boolean;
	allowEdit?: boolean;
	event?: Values<typeof PortActionTypes>;
	showPricePerTon?: boolean;
	title?: string;
	titleTooltip?: string | null;
}) => {
	const sortedRobs = sortByFuelGrade(robs, true);
	const robsWithKeys = sortedRobs
		.map((r) => {
			if (event === PortActionTypes.COMMENCEMENT) {
				return {
					...r,
					id: r.key,
					robBunkerId: r.id,
				};
			}

			return r;
		});

	let titleElement = null;

	if (typeof title === 'string') {
		titleElement = (
			<Flex align="center">
				<Typography.Title className={styles.tableTitle} level={4}>{title}</Typography.Title>
				{titleTooltip && (
					<TooltipIcon>
						{titleTooltip}
					</TooltipIcon>
				)}
			</Flex>
		);
	}

	return (
		<Flex vertical gap={5}>
			{titleElement}
			<Card slim className={styles.cardTable}>
				<EditableTable<IncomingBunkerArray[number], 'id'>
					key={robs.map((r) => r.key).join('_')}
					dataSource={sortByFuelGrade(robsWithKeys)}
					addNewText="Add entry"
					pagination={false}
					onSave={(id, row) => {
						return onSave(id, portCallActionId, row);
					}}
					emptyText="Empty"
					// @ts-ignore
					columns={getRobTableColumns(showPricePerTon)}
					keyDataIndex="id"
					allowAddNew={allowAddNew}
					size="small"
					onAddNew={(row) => onAddNew(portCallActionId, row)}
					enableDelete={allowDelete}
					enableEdit={allowEdit}
					onDelete={onDelete}
					iconButtons
					actionsTitle=""
					addButtonInHeader={false}
				/>
			</Card>
		</Flex>
	);
};

export const transformBunkers = (robs: RobBunkerProps[]) => {
	return robs
		.map((r) => ({
			id: r.id,
			key: r.id,
			portCallActionId: r.portCallActionId,
			quantity: calculateTotal(r.fuelQueue, (o) => o.quantity),
			fuelGrade: r.fuelGrade,
			pricePerTon: r?.fuelQueue[0]?.pricePerTon ?? 0,
			fuelQueue: r.fuelQueue,
		}))
		.sort((a, b) => a.fuelGrade.localeCompare(b.fuelGrade));
};

export const getRelevantBunkerTables = ({
	voyageDetails,
	robs,
	selectedEntry,
	onSave,
	onAddNew,
	onDelete,
	onRemoveFuelQueueItem,
	onSaveFuelQueueItem,
	allowAddNew,
	tcInCompletionBunkers,
	disabled = false,
} : {
	voyageDetails: GetVoyageDetailsResponse;
	robs: GetRobsFromPortCallResponse;
	selectedEntry: ItineraryPortCallDto;
	onSave: OnSaveFn;
	onAddNew: OnAddFn;
	onDelete: OnDeleteFn;
	onRemoveFuelQueueItem: OnRemoveFuelQueueItemFn;
	onSaveFuelQueueItem: OnSaveFuelQueueItem;
	allowAddNew: boolean;
	tcInCompletionBunkers: any[] | null;
	disabled?: boolean;
}) => {
	const isCommencement = isPortCallCommencementDto(selectedEntry);
	const isCompletion = isPortCallCompletionDto(selectedEntry);
	const isDelivery = isPortCallDeliveryDto(selectedEntry);
	const isRedelivery = isPortCallRedeliveryDto(selectedEntry);

	if (isCommencement) {
		const bunkers = transformBunkers(robs.commencement?.RobBunkers ?? []);
		const portCallActionId = robs?.commencement?.id;

		const fixtureType = voyageDetails?.previousVoyage?.fixture?.type;
		const hasPreviousOutVoyage = (
			fixtureType === FixtureTypes.TC_OUT ||
			fixtureType === FixtureTypes.SPOT
		);

		const fq: {
			id: number;
			key: string;
			portCallActionId: number;
			quantity: number;
			fuelGrade: Values<typeof FuelTypes>;
			pricePerTon: number;
		}[] = [];

		bunkers.forEach((b) => {
			const fuelQ = b.fuelQueue;
			fuelQ.forEach((f, index) => {
				fq.push({
					...f,
					fuelGrade: b.fuelGrade,
					key: `${b.id}_${b.fuelGrade}_${f.quantity}_${f.pricePerTon}_${index}`,
					portCallActionId: b.portCallActionId,
					id: b.id,
				});
			});
		});

		// Commencement bunkers need to be handled differently than "regular" bunkers,
		// including the need to add multiple entries of the same fueltype. This requires custom
		// handlers for both deletion and editing
		const onDeleteBunker = async (
			id: number | string | undefined,
			row: CommencementRobBunker,
		) => {
			await onRemoveFuelQueueItem(row);
		};

		const onUpdateBunker = async (
			id: string | number | undefined,
			_rId: number | undefined | null,
			row: CommencementRobBunker,
		) => {
			const relevantRobBunker = robs.commencement?.RobBunkers
				.find((rb) => rb.id === row.robBunkerId);
			
			const newQueue = [row];

			if (relevantRobBunker == null) {
				throw new Error('Could not update bunker');
			}

			await onSaveFuelQueueItem(portCallActionId, relevantRobBunker.id, newQueue);
		};

		if (hasPreviousOutVoyage || portCallActionId == null) {
			return null;
		}

		const disabledWithDate = disabled || voyageDetails.commencementDate == null;

		return (
			<>
				<Col span={24}>
					<Flex vertical gap={10}>
						{!voyageDetails.commencementDate && (
							<Alert type="warning" message="An act. departure date is needed before adding commencement bunkers"/>
						)}
						{getBunkerTable({
							allowAddNew: !disabledWithDate && allowAddNew,
							allowEdit: !disabledWithDate,
							allowDelete: !disabledWithDate,
							onAddNew,
							onDelete: onDeleteBunker,
							onSave: onUpdateBunker,
							robs: fq,
							title: 'Commencement',
							portCallActionId,
							showPricePerTon: true,
							event: PortActionTypes.COMMENCEMENT,
						})}
					</Flex>
				</Col>
			</>
		);
	}

	if (isCompletion) {
		const arrivalBunkers = transformBunkers(robs.arrival?.RobBunkers ?? []);
		const arrivalRobId = robs?.arrival?.id;
		const completionBunkers = transformBunkers(robs.completion?.RobBunkers ?? []);
		const completionRobId = robs?.completion?.id;

		const showArrival = selectedEntry.arrivalDate != null;
		const showCompletion = selectedEntry.departureDate != null;

		return (
			<>
				<Col span={12}>
					{!showArrival ? (
						<>
							<Alert
								message="NOTE: You must enter an arrival date if you wish to enter R.O.B entries for arrival"
								type="info"
								showIcon
							/>
							<br />
						</>
					) : (
						getBunkerTable({
							allowAddNew,
							onAddNew,
							onDelete,
							onSave,
							robs: arrivalBunkers,
							title: 'Arrival',
							portCallActionId: arrivalRobId,
						})
					)}
				</Col>
				<Col span={12}>
					{!showCompletion ? (
						<>
							<Alert
								message="NOTE: You must enter an departure date if you wish to enter R.O.B entries for completion"
								type="info"
								showIcon
							/>
							<br />
						</>
					) : (
						getBunkerTable({
							allowAddNew,
							onAddNew,
							onDelete,
							onSave,
							robs: completionBunkers,
							title: 'Completion',
							portCallActionId: completionRobId,
						})
					)}
				</Col>
			</>
		);
	}

	if (isDelivery) {
		const deliveryBunkers = transformBunkers(robs.delivery?.RobBunkers ?? []);
		const deliveryRobId = robs?.delivery?.id;

		return (
			<Col span={24}>
				{getBunkerTable({
					allowAddNew: false,
					allowEdit: false,
					allowDelete: false,
					onAddNew,
					onDelete,
					onSave,
					robs: deliveryBunkers,
					title: 'Delivery',
					portCallActionId: deliveryRobId,
					titleTooltip: 'Any changes to delivery bunkers should be made from the Expenses section, under Bunkers',
				})}
			</Col>
		);
	}

	if (isRedelivery) {
		const arrivalBunkers = transformBunkers(robs.arrival?.RobBunkers ?? []);
		const arrivalRobId = robs.arrival?.id;
		const redeliveryBunkers = transformBunkers(robs.redelivery?.RobBunkers ?? []);
		const redeliveryRobId = robs?.redelivery?.id;

		const showArrival = selectedEntry.arrivalDate != null;

		let redeliveryBunkersTable = (
			getBunkerTable({
				allowAddNew: false,
				allowEdit: false,
				allowDelete: false,
				onAddNew,
				onDelete,
				onSave,
				robs: redeliveryBunkers,
				title: 'Redelivery',
				portCallActionId: redeliveryRobId,
				titleTooltip: `Redelivery bunkers are fetched from the corresponding OUT estimate 
				and can only be edited from the \`Expenses\` tab or from the estimate itself`,
			})
		);

		if (tcInCompletionBunkers != null) {
			redeliveryBunkersTable = (
				getBunkerTable({
					allowAddNew: false,
					allowEdit: false,
					allowDelete: false,
					onAddNew,
					onDelete,
					onSave,
					robs: tcInCompletionBunkers,
					title: 'Redelivery',
					portCallActionId: null,
					showPricePerTon: true,
					titleTooltip: 'Redelivery bunkers are fetched from the corresponding IN estimate',
				})
			);
		}

		return (
			<>
				<Col span={12}>
					{!showArrival ? (
						<>
							<Alert
								message="NOTE: You must enter an arrival date if you wish to enter R.O.B entries for arrival"
								type="info"
								showIcon
							/>
							<br />
						</>
					) : (
						<>
							{getBunkerTable({
								allowAddNew: !disabled && allowAddNew,
								allowEdit: !disabled,
								allowDelete: !disabled,
								onAddNew,
								onDelete,
								onSave,
								robs: arrivalBunkers,
								title: 'Arrival',
								portCallActionId: arrivalRobId,
							})}
						</>
					)}
				</Col>
				<Col span={12}>{redeliveryBunkersTable}</Col>
			</>
		);
	}

	const arrivalBunkers = transformBunkers(robs.arrival?.RobBunkers ?? []);
	const departureBunkers = transformBunkers(robs.departure?.RobBunkers ?? []);
	const arrivalRobId = robs.arrival?.id;
	const departureRobId = robs.departure?.id;

	const showArrival = selectedEntry.arrivalDate != null;
	const showDeparture = selectedEntry.departureDate != null;

	return (
		<>
			<Col span={12}>
				{!showArrival ? (
					<>
						<Alert
							message="NOTE: You must enter an arrival date if you wish to enter R.O.B entries for arrival"
							type="info"
							showIcon
						/>
						<br />
					</>
				) : (
					getBunkerTable({
						allowAddNew: !disabled,
						allowEdit: !disabled,
						allowDelete: !disabled,
						onAddNew,
						onDelete,
						onSave,
						robs: arrivalBunkers,
						title: 'Arrival',
						portCallActionId: arrivalRobId,
					})
				)}
			</Col>
			<Col span={12}>
				{!showDeparture ? (
					<>
						<Alert
							message="NOTE: You must enter a departure date if you wish to enter R.O.B entries for departure"
							type="info"
							showIcon
						/>
						<br />
					</>
				) : (
					getBunkerTable({
						allowAddNew: !disabled && allowAddNew,
						allowEdit: !disabled,
						allowDelete: !disabled,
						onAddNew,
						onDelete,
						onSave,
						robs: departureBunkers,
						title: 'Departure',
						portCallActionId: departureRobId,
					})
				)}
			</Col>
		</>
	);
};
